import { LineChartSeries } from "@mantine/charts";

import { DropdownItem } from "../../Dropdown/types";
import { getColorFromIndex, getColorFromName } from "../utils";

import { formatCurrency } from "@/utils/formatEuro";

import type { Currency } from "@/types/currency";
import type { DatesRange } from "@/types/entity";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { TFunction } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import type { TimeframeRange } from "../../TimeframeInput/types";
import type { ChartProps } from "../interfaces/PerformanceChart.types";

export function getDefaultChartProps(currency: Currency): ChartProps {
  return {
    h: 300,
    valueFormatter: (value) =>
      value >= 0
        ? formatCurrency(value, currency.code, {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 0,
          })
        : "",
    withLegend: true,
  };
}

export function createChartConfig(
  currency: Currency,
  defaultChartProps: ChartProps,
  chartProps: ChartProps
) {
  const tooltipValueFormatter = (value: number) =>
    value >= 0
      ? formatCurrency(value, currency.code, {
          notation: "standard",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : "";

  const lineChartProps = {
    ...defaultChartProps,
    ...chartProps,
  };
  return { tooltipValueFormatter, lineChartProps };
}

export function getBenchmarkColorByName(visibleBenchmarks: IPerformanceChart[]) {
  return (name: string) => {
    const color = getColorFromName(name);
    if (!color) {
      const index = visibleBenchmarks.findIndex((b) => b.name === name);
      return getColorFromIndex(index);
    }
    return color;
  };
}

export function createTimeframeHandler(
  onTimeframeChange: ((range?: DatesRange) => void) | undefined
) {
  return (dates?: TimeframeRange) => {
    if (dates?.[0] && dates?.[1]) {
      onTimeframeChange?.({ from: dates[0] as string, to: dates[1] as string });
    } else {
      onTimeframeChange?.();
    }
  };
}

export function initializeBenchmark(
  currentBenchmark: string | undefined,
  compareMode: boolean,
  defaultBenchmark: string | undefined,
  setBenchmark: Dispatch<SetStateAction<string | undefined>>,
  dropdownBenchmarks: DropdownItem[]
) {
  return () => {
    // If a defaultBenchmark prop is provided and valid, use it.
    if (!currentBenchmark) {
      // Special-case: when used in compare mode, allow `__allCars__` as a default
      // which maps to the internal `__all__` option and shows "All cars" label.
      if (compareMode && defaultBenchmark === "__allCars__") {
        setBenchmark("__all__");
        return;
      }
      if (defaultBenchmark && dropdownBenchmarks.find((d) => d.value === defaultBenchmark)) {
        setBenchmark(defaultBenchmark);
        return;
      }

      // Otherwise default to the first dropdown option (index 0) when available
      if (dropdownBenchmarks.length > 0) {
        setBenchmark(dropdownBenchmarks[0].value);
      }
    }
  };
}

export function generateBenchmarkDropdown(
  hiddenBenchmarks: string[],
  benchmarks: IPerformanceChart[],
  restrictKpisByPlan: boolean,
  kpisDisabled: boolean,
  allLabel: string,
  t: TFunction<"translation", undefined>,
  compareMode: boolean,
  performance: IPerformanceChart | undefined
) {
  let dropdownBenchmarks: DropdownItem[] = [];
  const hiddenSet = new Set(hiddenBenchmarks || []);
  const filteredBenchmarks = benchmarks.filter((b) => !hiddenSet.has(b.name));
  const visibleName = (n?: string | number) => String(n ?? "").split("\u200B")[0];
  if (restrictKpisByPlan) {
    if (kpisDisabled) {
      dropdownBenchmarks = [
        {
          label: allLabel,
          value: "__all__",
        },
      ];
    } else {
      dropdownBenchmarks = filteredBenchmarks.map(({ name }) => {
        const v = visibleName(name);
        return {
          label: t(`components.PerformanceChart.benchmarks.${v}`, v),
          value: name,
        };
      });
      // In compare mode include the performance series (first car) as a selectable option
      if (compareMode && performance && performance.name && !hiddenSet.has(performance.name)) {
        if (!dropdownBenchmarks.find((d) => d.value === performance.name)) {
          const v = visibleName(performance.name);
          dropdownBenchmarks.unshift({
            label: t(`components.PerformanceChart.benchmarks.${v}`, v),
            value: performance.name,
          });
        }
      }
      if (filteredBenchmarks.length > 0 && !dropdownBenchmarks.find((d) => d.value === "__all__")) {
        dropdownBenchmarks.push({ label: allLabel, value: "__all__" });
      }
    }
  } else {
    dropdownBenchmarks = filteredBenchmarks.map(({ name }) => {
      const v = visibleName(name);
      return { label: t(`components.PerformanceChart.benchmarks.${v}`, v), value: name };
    });
    if (compareMode && performance && performance.name && !hiddenSet.has(performance.name)) {
      if (!dropdownBenchmarks.find((d) => d.value === performance.name)) {
        const v = visibleName(performance.name);
        dropdownBenchmarks.unshift({
          label: t(`components.PerformanceChart.benchmarks.${v}`, v),
          value: performance.name,
        });
      }
    }
    if (filteredBenchmarks.length > 0 && !dropdownBenchmarks.find((d) => d.value === "__all__")) {
      dropdownBenchmarks.push({ label: allLabel, value: "__all__" });
    }
  }
  return { filteredBenchmarks, dropdownBenchmarks, visibleName, hiddenSet };
}

export function generateChartData(
  performance: IPerformanceChart | undefined,
  visibleBenchmarks: IPerformanceChart[],
  chart: Record<string, any>[],
  series: LineChartSeries[],
  visibleName: (n?: string | number) => string,
  getBenchmarkColor: (name: string) => string,
  t: TFunction<"translation", undefined>,
  currentBenchmark: string | undefined,
  compareMode: boolean,
  chartColor: string,
  showPerformanceWhenBenchmarkSelected: boolean,
  hiddenSet: Set<string>
) {
  if (performance) {
    // If performance has no data but there are visible benchmarks, prefer rendering benchmarks
    const perfHasData = (performance.data?.length ?? 0) > 0;
    if (!perfHasData && visibleBenchmarks.length > 0) {
      // Build chart from benchmarks (use the longest benchmark as reference)
      const reference = visibleBenchmarks.reduce((prev, cur) =>
        cur.data.length > prev.data.length ? cur : prev
      );

      chart = reference.data.map(({ date }, index) => {
        const item: Record<string, number | string> = { date };
        visibleBenchmarks.forEach((benchmark) => {
          item[benchmark.name] = benchmark.data[index]?.value ?? 0;
        });
        return item;
      });

      series = visibleBenchmarks.map((benchmark) => {
        const v = visibleName(benchmark.name);
        return {
          color: getBenchmarkColor(benchmark.name),
          name: benchmark.name,
          label: t(`components.PerformanceChart.benchmarks.${v}`, v),
        };
      }) as LineChartSeries[];
    } else {
      if (currentBenchmark === "__all__") {
        chart = performance.data.map(({ date, value }, index) => {
          const item: Record<string, number | string> = { date, [performance.name]: value };
          visibleBenchmarks.forEach((benchmark) => {
            item[benchmark.name] = benchmark.data[index]?.value ?? 0;
          });
          return item;
        });

        series = [
          ...visibleBenchmarks.map((benchmark) => {
            const v = visibleName(benchmark.name);
            return {
              color: getBenchmarkColor(benchmark.name),
              name: benchmark.name,
              label: t(`components.PerformanceChart.benchmarks.${v}`, v),
            };
          }),
          {
            color: "primary.5",
            name: performance.name,
            label: t(
              `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
              visibleName(performance.name)
            ),
          },
        ] as LineChartSeries[];
      } else {
        // If in compareMode, selecting a specific car should show only that car.
        if (compareMode) {
          // Selecting the performance series (first car)
          if (currentBenchmark === performance.name) {
            chart = (performance.data || []).map(({ date, value }) => ({
              date,
              [performance.name]: value,
            }));

            series = [
              {
                color: chartColor,
                name: performance.name,
                label: t(
                  `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
                  visibleName(performance.name)
                ),
              },
            ] as LineChartSeries[];
          } else {
            // Selecting another benchmark (other cars)
            const benchmark = visibleBenchmarks.find(({ name }) => name === currentBenchmark);
            const reference = benchmark || visibleBenchmarks[0] || { data: [] as any[] };

            chart = (reference.data || []).map(({ date }, index) => ({
              date,
              [reference.name]: benchmark?.data[index]?.value ?? 0,
            }));

            series = [
              {
                color: getBenchmarkColor(reference.name),
                name: reference.name,
                label: t(
                  `components.PerformanceChart.benchmarks.${visibleName(reference.name)}`,
                  visibleName(reference.name)
                ),
              },
            ] as LineChartSeries[];
          }
        } else {
          const benchmark = visibleBenchmarks.find(({ name }) => name === currentBenchmark);
          chart = performance.data.map(({ date, value }, index) => ({
            date,
            [performance.name]: value,
            [currentBenchmark || "none"]: benchmark?.data[index]?.value ?? 0,
          }));

          series = [
            currentBenchmark && {
              color: getBenchmarkColor(currentBenchmark),
              name: currentBenchmark,
              label: t(
                `components.PerformanceChart.benchmarks.${visibleName(currentBenchmark)}`,
                visibleName(currentBenchmark)
              ),
            },
            showPerformanceWhenBenchmarkSelected && {
              color: chartColor,
              name: performance.name,
              label: t(
                `components.PerformanceChart.benchmarks.${visibleName(performance.name)}`,
                visibleName(performance.name)
              ),
            },
          ].filter(Boolean) as LineChartSeries[];
        }
      }
    }
  }

  // Strip out any hidden benchmark keys from the chart data and series
  const cleanedChart = chart.map((item) => {
    const copy: Record<string, any> = { ...item };
    Object.keys(copy).forEach((key) => {
      if (hiddenSet.has(key)) delete copy[key];
    });
    return copy;
  });
  return { cleanedChart, chart, series };
}
